package game

import EntityDef
import com.soywiz.korim.color.RGBA
import com.soywiz.korio.dynamic.mapper.ObjectMapper
import view.TileTemplatePalette

fun wishlairObjectMapper(): ObjectMapper {
    val mapper = ObjectMapper()
    mapper.registerType(Room::class) {
        val room = Room(
            it["level"].gen(Int::class),
            it["mapX"].gen(Int::class),
            it["mapY"].gen(Int::class),
            it["exitNorth"].gen(String::class),
            it["exitSouth"].gen(String::class),
            it["exitEast"].gen(String::class),
            it["exitWest"].gen(String::class),
            it["dark"].gen(Boolean::class),
        )

        room.entityDef.addAll(it["entities"].genList<EntityDef>())
        room.tiles = it["tiles"].genList<Tile>()

        room
    }
    mapper.registerUntype(Room::class) {
        mapOf(
            "level" to it.level.gen(),
            "mapX" to it.mapX.gen(),
            "mapY" to it.mapY.gen(),
            "exitNorth" to it.exitNorth.gen(),
            "exitSouth" to it.exitSouth.gen(),
            "exitEast" to it.exitEast.gen(),
            "exitWest" to it.exitWest.gen(),
            "dark" to it.dark.gen(),
            "entities" to it.entityDef.gen(),
            "tiles" to it.tiles.gen(),
        )
    }

    mapper.registerType(Tile::class) {
        Tile(
            it["char"].gen(Int::class),
            it["fgColor"].gen(RGBA::class),
            it["bgColor"].gen(RGBA::class),
            it["type"]?.gen(TileType::class) ?: TileType.Floor
        )
    }
    mapper.registerUntype(Tile::class) {
        mapOf(
            "char" to it.char.gen(),
            "fgColor" to it.fgColor.gen(),
            "bgColor" to it.bgColor.gen(),
            "type" to it.type.gen(),
        )
    }

    mapper.registerType(EntityDef::class) {
        val width = it["width"].gen(Int::class)
        val height = it["height"].gen(Int::class)

        EntityDef(
            it["id"].gen(String::class),
            it["type"].gen(String::class),
            it["col"].gen(Int::class),
            it["row"].gen(Int::class),
            if (width != 0) width else 1,
            if (height != 0) height else 1,
            it["params"].gen(String::class),
            it["tag"].gen(String::class),
            it["reset"].gen(Boolean::class),
        )
    }
    mapper.registerUntype(EntityDef::class) {
        mapOf(
            "id" to it.id.gen(),
            "type" to it.type.gen(),
            "col" to it.col.gen(),
            "row" to it.row.gen(),
            "width" to it.width.gen(),
            "height" to it.height.gen(),
            "params" to it.params.gen(),
            "tag" to it.tag.gen(),
            "reset" to it.tag.gen(),
        )
    }

    mapper.registerType(RGBA::class) {
        RGBA(
            it["v"].gen(Int::class),
        )
    }
    mapper.registerUntype(RGBA::class) {
        mapOf(
            "v" to it.value.gen()
        )
    }

    mapper.registerEnum(TileType::class, TileType.values())
    mapper.registerUntypeEnum(TileType::class)

    mapper.registerType(TileTemplatePalette.TileList::class) {
        TileTemplatePalette.TileList(
            it["tiles"].genList(Tile::class)
        )
    }
    mapper.registerUntype(TileTemplatePalette.TileList::class) {
        mapOf(
            "tiles" to it.tiles.gen()
        )
    }

    mapper.registerEnum(LightSource::class, LightSource.values())
    mapper.registerUntypeEnum(LightSource::class)

    mapper.registerEnum(Weapon::class, Weapon.values())
    mapper.registerUntypeEnum(Weapon::class)

    mapper.registerEnum(Armor::class, Armor.values())
    mapper.registerUntypeEnum(Armor::class)

    mapper.registerEnum(Bracer::class, Bracer.values())
    mapper.registerUntypeEnum(Bracer::class)

    mapper.registerEnum(Jump::class, Jump.values())
    mapper.registerUntypeEnum(Jump::class)

    mapper.registerEnum(SpeedUp::class, SpeedUp.values())
    mapper.registerUntypeEnum(SpeedUp::class)

    mapper.registerType(GameState::class) {
        val saveGame = GameState()

        saveGame.shards = it["shards"].gen(Int::class)
        saveGame.playerLife = it["playerLife"].gen(Int::class)
        saveGame.playerMaxLife = it["playerMaxLife"].gen(Int::class)
        saveGame.playerCol = it["playerCol"].gen(Int::class)
        saveGame.playerRow = it["playerRow"].gen(Int::class)
        saveGame.roomId = it["roomId"].gen(String::class)
        saveGame.lens = it["lens"].gen(Boolean::class)
        saveGame.map = it["map"].gen(Boolean::class)
        saveGame.shardScanner = it["shardScanner"].gen(Boolean::class)
        saveGame.loreScanner = it["loreScanner"].gen(Boolean::class)
        saveGame.itemScanner = it["itemScanner"].gen(Boolean::class)
        saveGame.flippers = it["flippers"].gen(Boolean::class)
        saveGame.lightSource = it["lightSource"].gen(LightSource::class)
        saveGame.weapon = it["weapon"].gen(Weapon::class)
        saveGame.armor = it["armor"].gen(Armor::class)
        saveGame.bracer = it["bracer"].gen(Bracer::class)
        saveGame.jump = it["jump"].gen(Jump::class)
        saveGame.speed = it["speed"].gen(SpeedUp::class)
        saveGame.lavaBoots = it["lavaBoots"].gen(Boolean::class)
        saveGame.divineBoots = it["divineBoots"].gen(Boolean::class)
        saveGame.permanentlyRemoved = it["permanentlyRemoved"].genSet<String>()
        saveGame.lore = it["lore"].genSet<String>()
        saveGame.discovered = it["discovered"].genSet<String>()

        saveGame
    }
    mapper.registerUntype(GameState::class) {
        mapOf(
            "shards" to it.shards,
            "playerLife" to it.playerLife,
            "playerMaxLife" to it.playerMaxLife,
            "playerCol" to it.playerCol,
            "playerRow" to it.playerRow,
            "roomId" to it.roomId,
            "lens" to it.lens,
            "map" to it.map,
            "shardScanner" to it.shardScanner,
            "loreScanner" to it.loreScanner,
            "itemScanner" to it.itemScanner,
            "flippers" to it.flippers,
            "lightSource" to it.lightSource.name,
            "weapon" to it.weapon.name,
            "armor" to it.armor.name,
            "bracer" to it.bracer.name,
            "jump" to it.jump.name,
            "speed" to it.speed.name,
            "lavaBoots" to it.lavaBoots,
            "divineBoots" to it.divineBoots,
            "permanentlyRemoved" to it.permanentlyRemoved,
            "lore" to it.lore,
            "discovered" to it.discovered,
        )
    }

    mapper.registerType(GameSettings::class) {
        val settings = GameSettings()

        settings.mute = it["mute"].gen(Boolean::class)
        settings.muteSteps = it["muteSteps"].gen(Boolean::class)
        settings.fullScreen = it["fullScreen"].gen(Boolean::class)
        settings.keyboard = it["keyboard"].gen(Keyboard::class)
        settings.language = it["language"].gen(Language::class)

        settings
    }
    mapper.registerUntype(GameSettings::class) {
        mapOf(
            "mute" to it.mute,
            "muteSteps" to it.muteSteps,
            "fullScreen" to it.fullScreen,
            "keyboard" to it.keyboard.name,
            "language" to it.language.name,
        )
    }

    mapper.registerEnum(Keyboard::class, Keyboard.values())
    mapper.registerUntypeEnum(Keyboard::class)

    mapper.registerEnum(Language::class, Language.values())
    mapper.registerUntypeEnum(Language::class)

    return mapper
}

